import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, X } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import {
  getActiveLiveUploads,
  discardLiveUpload,
} from '../../live/liveUploadService';
import { ActiveLiveUploadResponse } from '../../live/types';
import { Card, CardContent } from '../../../components/ui/card';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { TableRow, TableCell } from '../../../components/ui/table';
import { Button } from '../../../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import DefaultThumbnail from '../../../assets/recording/WhiteThumbnail.svg';

interface LiveRecordingsBannerProps {
  teamId: string;
  folderId: string;
  view?: 'grid' | 'list';
}

const LiveRecordingsBanner: React.FC<LiveRecordingsBannerProps> = ({
  folderId,
  view = 'grid',
}) => {
  const navigate = useNavigate();
  const [liveRecordings, setLiveRecordings] = useState<
    ActiveLiveUploadResponse[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [recordingToDiscard, setRecordingToDiscard] =
    useState<ActiveLiveUploadResponse | null>(null);
  const [isDiscarding, setIsDiscarding] = useState(false);

  useEffect(() => {
    let isActive = true;

    const fetchLiveRecordings = async () => {
      try {
        setLoading(true);

        const recordings = await getActiveLiveUploads();

        // Only update state if component is still mounted
        if (isActive) {
          setLiveRecordings(recordings);
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch active live recordings:', error);
        if (isActive) {
          setLoading(false);
        }
      }
    };

    // Fetch once on mount only
    fetchLiveRecordings();

    return () => {
      isActive = false;
    };
  }, []); // Empty dependency array - only run once on mount

  if (loading || liveRecordings.length === 0) {
    return null;
  }

  const handleResumeRecording = (recording: ActiveLiveUploadResponse) => {
    const targetFolderId = recording.folderId || folderId;
    navigate(
      `/live/${recording.teamId}/${targetFolderId}/${recording.liveUploadId}`,
    );
  };

  const handleDiscardClick = (
    e: React.MouseEvent,
    recording: ActiveLiveUploadResponse,
  ) => {
    e.stopPropagation(); // Prevent card click from triggering
    setRecordingToDiscard(recording);
    setDiscardDialogOpen(true);
  };

  const handleConfirmDiscard = async () => {
    if (!recordingToDiscard) return;

    setIsDiscarding(true);
    try {
      await discardLiveUpload(recordingToDiscard.liveUploadId);

      // Remove from local state
      setLiveRecordings((prev) =>
        prev.filter((r) => r.liveUploadId !== recordingToDiscard.liveUploadId),
      );

      toast('Recording discarded', {
        description: 'The live recording has been deleted.',
      });
    } catch (error) {
      console.error('Failed to discard recording:', error);
      toast.error('Failed to discard', {
        description: 'Could not delete the recording. Please try again.',
      });
    } finally {
      setIsDiscarding(false);
      setDiscardDialogOpen(false);
      setRecordingToDiscard(null);
    }
  };

  const getCreatedAtString = (createdAt: string) => {
    const now = moment();
    const created = moment(createdAt);

    if (!created.isValid()) {
      return createdAt;
    }

    if (created.isSame(now, 'day')) {
      return 'Today';
    }

    if (created.isSame(now.subtract(1, 'days'), 'day')) {
      return 'Yesterday';
    }

    if (created.isSame(now.subtract(2, 'days'), 'day')) {
      return '2 days ago';
    }

    if (created.isSame(now.subtract(3, 'days'), 'day')) {
      return '3 days ago';
    }

    return created.format('MMM D, YYYY');
  };

  // Grid View
  if (view === 'grid') {
    return (
      <>
        {liveRecordings.map((recording) => (
          <Card
            key={recording.liveUploadId}
            className="relative flex flex-col h-full shadow-none bg-transparent border-0 transition-all duration-200 ease-out transform scale-100 hover:scale-102 cursor-pointer [container-type:inline-size]"
            onClick={() => handleResumeRecording(recording)}
          >
            <div className="relative">
              <div className="w-full aspect-video bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-950 dark:to-blue-900 rounded-lg overflow-hidden relative">
                <img
                  src={DefaultThumbnail}
                  alt="Live Recording"
                  className="w-full h-full object-cover opacity-10"
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                  <div className="relative">
                    {(!recording.status || recording.status === 'active') && (
                      <div className="w-12 h-12 bg-blue-500/20 rounded-full animate-ping absolute inset-0" />
                    )}
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center relative">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                    {recording.status === 'paused'
                      ? 'Recording Paused'
                      : 'Recording in Progress'}
                  </span>
                </div>
                {/* Action buttons */}
                <div className="absolute top-2 right-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 bg-white/90 hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-800 text-red-600 hover:text-red-700"
                    onClick={(e) => handleDiscardClick(e, recording)}
                    title="Discard recording"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            <CardContent className="flex-grow flex flex-col justify-start py-4 px-0 last:pb-4 bg-transparent">
              <div className="flex items-start">
                <Avatar className="w-8 h-8 bg-blue-500 transition-transform duration-200 ease-out">
                  <AvatarFallback className="bg-blue-500">
                    <Clock className="text-sm text-white" size={16} />
                  </AvatarFallback>
                </Avatar>
                <div className="ml-2 flex-grow overflow-hidden">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold overflow-hidden text-ellipsis whitespace-nowrap">
                      Live Recording
                    </p>
                    {recording.sessionCount && recording.sessionCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                      >
                        {recording.sessionCount}{' '}
                        {recording.sessionCount === 1 ? 'session' : 'sessions'}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {getCreatedAtString(recording.createdAt)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Discard confirmation dialog */}
        <AlertDialog
          open={discardDialogOpen}
          onOpenChange={setDiscardDialogOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard recording?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently delete this live recording and all its
                sessions. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDiscarding}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDiscard}
                disabled={isDiscarding}
                className="bg-red-600 hover:bg-red-700"
              >
                {isDiscarding ? 'Discarding...' : 'Discard'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  // List View
  return (
    <>
      {liveRecordings.map((recording) => (
        <TableRow
          key={recording.liveUploadId}
          className="cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => handleResumeRecording(recording)}
        >
          <TableCell className="w-12 pl-4">
            <div className="w-10 h-10 bg-blue-500 rounded flex items-center justify-center relative overflow-hidden">
              {(!recording.status || recording.status === 'active') && (
                <div className="w-10 h-10 bg-blue-500/20 rounded absolute animate-ping" />
              )}
              <Clock className="w-5 h-5 text-white relative z-10" />
            </div>
          </TableCell>
          <TableCell className="font-medium">
            <div className="flex items-center gap-2">
              <span>Live Recording</span>
              {recording.sessionCount && recording.sessionCount > 0 && (
                <Badge
                  variant="secondary"
                  className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                >
                  {recording.sessionCount}{' '}
                  {recording.sessionCount === 1 ? 'session' : 'sessions'}
                </Badge>
              )}
            </div>
          </TableCell>
          <TableCell className="text-muted-foreground">
            {getCreatedAtString(recording.createdAt)}
          </TableCell>
          <TableCell className="text-muted-foreground">-</TableCell>
          <TableCell className="text-muted-foreground">-</TableCell>
          <TableCell>
            <Badge
              variant="secondary"
              className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
            >
              {recording.status === 'paused' ? 'Paused' : 'In Progress'}
            </Badge>
          </TableCell>
          <TableCell className="text-right">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
              onClick={(e) => handleDiscardClick(e, recording)}
              title="Discard recording"
            >
              <X className="h-4 w-4" />
            </Button>
          </TableCell>
        </TableRow>
      ))}

      {/* Discard confirmation dialog */}
      <AlertDialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard recording?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this live recording and all its
              sessions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDiscarding}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDiscard}
              disabled={isDiscarding}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDiscarding ? 'Discarding...' : 'Discard'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default LiveRecordingsBanner;
